import { jsx } from "react/jsx-runtime";
import { iconRegistry } from "@pcln/horizon-icons";
import { cn } from "../../utils/index.js";
import { CdnIcon } from "../CdnIcon/index.js";
import { ContentfulImgOptimizer } from "../ContentfulImgOptimizer/ContentfulImgOptimizer.js";
import { HorizonIcon } from "../HorizonIcon/index.js";
import { Image } from "../Image/index.js";
import { discVariants } from "./Disc.variants.js";
const DISC_SIZE_PX = {
    '2xs': 24,
    xs: 32,
    sm: 40,
    md: 48,
    lg: 72
};
const DISC_IMG_FIT_SIZE_PX = {
    '2xs': 16,
    xs: 20,
    sm: 24,
    md: 32,
    lg: 48
};
const DISC_IMG_FIT_SIZE = {
    '2xs': 4,
    xs: 5,
    sm: 6,
    lg: 12
};
const DISC_IMG_FIT_SIZE_CLASS = {
    4: 'w-4 h-4',
    5: 'w-5 h-5',
    6: 'w-6 h-6',
    8: 'w-8 h-8',
    12: 'w-12 h-12'
};
const Disc = ({ alt, className, content, contentType, emphasis, isVertical, hasMask, palette, size })=>{
    const { baseSlot, iconSlot } = discVariants({
        contentType,
        emphasis,
        hasMask,
        isVertical,
        palette,
        size
    });
    const getIconSize = (size)=>{
        if ('2xs' === size) return '16';
        if ('xs' === size) return '18';
        if ('sm' === size) return '20';
        if ('md' === size) return '24';
        if ('lg' === size) return '36';
        return '24';
    };
    const isContentfulImage = (c)=>'object' == typeof c && null !== c && 'contentfulUrl' in c;
    const getImageSrcAndAlt = (c, fallbackAlt)=>{
        if ('string' == typeof c) return {
            src: c,
            alt: fallbackAlt
        };
        if ('object' == typeof c && null !== c && 'src' in c) return {
            src: c.src,
            alt: c.alt ?? fallbackAlt
        };
        throw new Error('ContentfulImage must be rendered via ContentfulImgOptimizer, not getImageSrcAndAlt');
    };
    const renderIconContent = ()=>{
        const calculatedIconSize = getIconSize(size);
        const isHorizonIcon = 'string' == typeof content && Object.hasOwn(iconRegistry, content);
        if (isHorizonIcon) return /*#__PURE__*/ jsx(HorizonIcon, {
            name: content,
            palette: palette,
            size: calculatedIconSize,
            className: iconSlot()
        });
        return /*#__PURE__*/ jsx(CdnIcon, {
            className: iconSlot(),
            iconName: content,
            size: calculatedIconSize
        });
    };
    const renderImgFillContent = ()=>{
        const px = DISC_SIZE_PX[size ?? 'md'] ?? 48;
        if (isContentfulImage(content)) return /*#__PURE__*/ jsx(ContentfulImgOptimizer, {
            contentfulUrl: content.contentfulUrl,
            alt: content.alt,
            fixedWidth: px,
            fixedHeight: px,
            width: "full",
            height: "full",
            objectFit: "cover"
        });
        const { src, alt: imgAlt } = getImageSrcAndAlt(content, alt ?? 'Disc Image');
        return /*#__PURE__*/ jsx(Image, {
            src: src,
            alt: imgAlt,
            width: "full",
            height: "full",
            objectFit: "cover"
        });
    };
    const renderImgFitContent = ()=>{
        const px = DISC_IMG_FIT_SIZE_PX[size ?? 'md'] ?? 32;
        const imgFitSize = DISC_IMG_FIT_SIZE[size ?? 'md'] ?? 8;
        const sizeClass = DISC_IMG_FIT_SIZE_CLASS[imgFitSize] ?? 'w-8 h-8';
        if (isContentfulImage(content)) return /*#__PURE__*/ jsx("div", {
            className: cn('flex items-center justify-center', sizeClass),
            children: /*#__PURE__*/ jsx(ContentfulImgOptimizer, {
                contentfulUrl: content.contentfulUrl,
                alt: content.alt,
                fixedWidth: px,
                fixedHeight: px,
                width: "full",
                height: "full",
                objectFit: "contain"
            })
        });
        const { src, alt: imgAlt } = getImageSrcAndAlt(content, alt ?? 'Disc Image');
        return /*#__PURE__*/ jsx(Image, {
            src: src,
            alt: imgAlt,
            width: imgFitSize,
            height: imgFitSize,
            objectFit: "contain"
        });
    };
    const renderInitialsContent = ()=>{
        const text = 'string' == typeof content ? content : '';
        const getInitials = (s)=>(s?.length === 2 ? s : s?.trim()?.slice(0, 2))?.toUpperCase() ?? '';
        return getInitials(text);
    };
    const renderContent = ()=>{
        switch(contentType){
            case 'icon':
                return renderIconContent();
            case 'imgFill':
                return renderImgFillContent();
            case 'imgFit':
                return renderImgFitContent();
            case 'initials':
            default:
                return renderInitialsContent();
        }
    };
    return /*#__PURE__*/ jsx("div", {
        className: cn(baseSlot(), className),
        children: renderContent()
    });
};
export { Disc };
